import { useEffect, useState } from "react";
import { adminApi, adminToken } from "../lib/contentApi";
import { errorStatSay, bookPageStatSay, audioStatSay, zoneSay } from "@greg-chick/shared";
import { DocentCost } from "./DocentCost";
import { statsCsv, statsCsvName } from "../lib/statsCsv";

/**
 * **방문자수** — 비식별 집계 보기.
 *
 * ## 여기 없는 것
 * 누가 왔는지, 어느 기기인지, 어디서 왔는지는 **없다.** 저장 자체를 안 한다(불변식 1).
 * 그래서 이 화면은 "몇 번"만 말할 수 있고, 그게 전부다 — 그 이상을 기대하지 않게
 * 화면에서도 그렇게 적는다.
 *
 * ## 🔴 이름은 「방문자수」지만 실제로는 **횟수**다
 * 같은 아이가 두 번 들어오면 2로 센다. 한 사람을 알아보려면 기기에 표식을 남겨야 하는데,
 * 그게 바로 안 하기로 한 것이다(불변식 1). **정확한 사람 수는 Cloudflare 웹 분석·GA4** 가 센다.
 * 그래서 화면 안에 그 차이를 적어 둔다 — 이름만 보고 사람 수로 오해하면 판단이 틀어진다.
 */
interface Row {
  day: string;
  name: string;
  key: string;
  n: number;
}

const LABEL: Record<string, string> = {
  // 🔴 예전 이름표가 「체험공간 입장」이었다 — 실제로 세는 것은 **첫 화면이 떴다**이다.
  //    이름표가 틀리면 운영자가 **잘못된 숫자로 판단한다**(2026-08-27 집계 자체는 고쳤다).
  visit: "방문(첫 화면 열림)",
  zone_enter: "존에 들어감",
  book_page: "전자책 쪽 넘김",
  book_done: "📖 책 끝까지 봄",
  world_enter: "🗺️ 체험공간 들어감",
  entry_click: "🏠 첫 화면에서 누름",
  goods_view: "굿즈 열어봄",
  goods_click: "🛒 사러 가기 누름",
  ar_open: "📷 카메라 마법 열어봄",
  ar_fallback: "📷 카메라 대신 영상으로",
  ar_photo: "📸 포토존(열기→카메라→찍기→저장)",
  audio_play: "🎧 읽어주기 들음",
  object_open: "이야기·영상 열어봄",
  hero_made: "히어로 만듦",
  app_error: "🔴 화면이 깨짐",
  // 💰 돈이 걸린 숫자 — 공급자별(anthropic·openai·google)이 실제 호출, fallback 은 되돌아간 것
  npc_ask: "🤖 AI 자유 대화(AI별 · fallback=버튼 답으로 되돌아감)",
};

/** 📸 포토존 단계 — 어디서 멈추는지가 고칠 곳을 가리킨다(못 찍음 ↔ 안 남김). */
const PHOTO_LABEL: Record<string, string> = {
  open: "① 열었다",
  // ①과 ② 사이가 벌어지면 **카메라 권한·기기 문제**다(안내 문구를 고칠 자리)
  camera: "② 카메라가 켜졌다",
  capture: "③ 찍었다",
  save: "④ 저장했다",
  share: "④ 보냈다",
};

/** 기간 고르기 — 최근 N일 · 전체 · 직접 지정. */
type Period = { kind: "days"; days: number } | { kind: "all" } | { kind: "range"; from: string; to: string };

const PRESETS: { label: string; p: Period }[] = [
  { label: "오늘", p: { kind: "days", days: 1 } },
  { label: "7일", p: { kind: "days", days: 7 } },
  { label: "30일", p: { kind: "days", days: 30 } },
  { label: "90일", p: { kind: "days", days: 90 } },
  { label: "전체", p: { kind: "all" } },
];

/** 서버에 보낼 질의 문자열. 서버가 날짜 모양을 다시 검사한다(불변식 7). */
function toQuery(p: Period): string {
  if (p.kind === "all") return "all=1";
  if (p.kind === "days") return `days=${p.days}`;
  const parts: string[] = [];
  if (p.from) parts.push(`from=${p.from}`);
  if (p.to) parts.push(`to=${p.to}`);
  // 둘 다 비었으면 아무것도 안 건 것과 같다 — 기본(30일)으로 떨어지지 않게 전체로 본다
  return parts.length ? parts.join("&") : "all=1";
}

/**
 * 이 기기 달력의 오늘(YYYY-MM-DD). 🔴 예전 `toISOString()` 은 UTC 라 한국 새벽 0~9시엔 **어제**가 되어,
 * 날짜 고르기에서 오늘을 못 골랐다(2026-10-11). 집계 저장은 UTC 그대로다(아래 안내 문구).
 */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * 지금 무슨 기간인지 사람 말로 — 보고서에 옮길 때 **어느 기간 숫자인지**가 같이 가야 한다.
 * 누계를 기간 값으로 잘못 옮기는 일이 QR 집계에서 실제로 있었다.
 */
function periodSay(p: Period): string {
  if (p.kind === "all") return "지금까지 전체";
  if (p.kind === "days") return p.days === 1 ? "오늘" : `최근 ${p.days}일`;
  return `${p.from || "처음"} ~ ${p.to || "오늘"}`;
}

export function StatsPanel() {
  const [period, setPeriod] = useState<Period>({ kind: "days", days: 30 });
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [state, setState] = useState<"loading" | "ok" | "fail">("loading");
  const query = toQuery(period);

  useEffect(() => {
    let alive = true;
    setState("loading");
    fetch(`${adminApi()}/admin/stats?${query}`, {
      headers: { "x-admin-token": adminToken() },
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { days?: Row[]; totals?: Record<string, number> }) => {
        if (!alive) return;
        setRows(d.days ?? []);
        setTotals(d.totals ?? {});
        setState("ok");
      })
      .catch(() => alive && setState("fail"));
    return () => {
      alive = false;
    };
  }, [query]);

  /** 무엇을 많이 봤나 — 이름+키로 묶어 큰 것부터. */
  const top = (name: string, limit = 6) => {
    const acc = new Map<string, number>();
    for (const r of rows.filter((x) => x.name === name)) {
      acc.set(r.key, (acc.get(r.key) ?? 0) + r.n);
    }
    return [...acc.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
  };

  return (
    <section className="stats">
      <div className="stats__head">
        <h2>📊 방문자수</h2>
        <div className="seg" role="group" aria-label="기간">
          {PRESETS.map(({ label, p }) => (
            <button
              key={label}
              type="button"
              className="seg__btn"
              aria-pressed={
                (p.kind === "all" && period.kind === "all") ||
                (p.kind === "days" && period.kind === "days" && period.days === p.days)
              }
              onClick={() => setPeriod(p)}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className="seg__btn"
            aria-pressed={period.kind === "range"}
            onClick={() => setPeriod({ kind: "range", from, to })}
          >
            날짜 지정
          </button>
        </div>
      </div>

      {period.kind === "range" ? (
        <div className="stats__range">
          <label>
            부터
            <input
              type="date"
              value={from}
              max={to || today()}
              onChange={(e) => {
                setFrom(e.target.value);
                setPeriod({ kind: "range", from: e.target.value, to });
              }}
            />
          </label>
          <label>
            까지
            <input
              type="date"
              value={to}
              min={from}
              max={today()}
              onChange={(e) => {
                setTo(e.target.value);
                setPeriod({ kind: "range", from, to: e.target.value });
              }}
            />
          </label>
        </div>
      ) : null}

      <p className="stats__note">
        <b>누가 왔는지는 저장하지 않아요.</b> 몇 번 일어났는지만 세요 — 같은 아이가 두 번 들어오면 2로 세요.
        날짜는 <b>세계 표준시(UTC)</b>로 나눠요 — 한국 시각으로는 <b>오전 9시에 하루가 바뀌어</b>, 새벽 0~9시에 쓴 것은 전날에 들어가요.
        정확한 사람 수가 필요하면 Cloudflare 웹 분석을 켜세요.
      </p>

      {state === "loading" ? <p className="stats__empty">불러오는 중…</p> : null}
      {state === "fail" ? <p className="stats__empty">숫자를 못 불러왔어요. 잠시 뒤 다시 열어 보세요.</p> : null}

      {state === "ok" && rows.length === 0 ? (
        <p className="stats__empty">아직 쌓인 숫자가 없어요. 아이들이 다녀가면 여기에 나와요.</p>
      ) : null}

      {state === "ok" && rows.length > 0 ? (
        <>
          {/* 📄 결과보고·조치계획서 칸에 옮길 숫자 — 손으로 옮기다 누계를 기간 값으로 적는 일이 있었다. 파일 첫 줄에 기간이 박힌다 */}
          <p className="stats__export">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                const blob = new Blob([statsCsv(rows, totals, (k) => LABEL[k] ?? k, periodSay(period))], {
                  type: "text/csv;charset=utf-8",
                });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = statsCsvName(periodSay(period));
                a.click();
                URL.revokeObjectURL(a.href);
              }}
            >
              📄 이 기간 숫자 내려받기(엑셀)
            </button>
            <span className="hint">「{periodSay(period)}」 합계와 날짜별 숫자가 한 파일에 들어가요.</span>
          </p>
          <div className="stats__cards">
            {Object.entries(totals)
              .sort((a, b) => b[1] - a[1])
              .map(([k, n]) => (
                <div className="stats__card" key={k}>
                  <span className="stats__n">{n.toLocaleString()}</span>
                  <span className="stats__label">{LABEL[k] ?? k}</span>
                </div>
              ))}
          </div>

          {(["app_error", "goods_click", "book_done", "entry_click", "ar_fallback", "ar_photo", "audio_play", "zone_enter", "object_open", "goods_view", "book_page"] as const).map((name) => {
            const list = top(name);
            if (list.length === 0) return null;
            return (
              <div className="stats__group" key={name}>
                <h3>{LABEL[name]}</h3>
                <ul className="stats__list">
                  {list.map(([key, n]) => (
                    <li key={key || "-"}>
                      {name === "app_error" ? (
                        <ErrorSay statKey={key} />
                      ) : name === "book_page" ? (
                        <BookPageSay statKey={key} />
                      ) : name === "audio_play" ? (
                        <AudioSay statKey={key} />
                      ) : (
                        <span>
                          {name === "zone_enter"
                            ? zoneSay(key)
                            : name === "ar_photo"
                              ? (PHOTO_LABEL[key] ?? key)
                              : key || "(없음)"}
                        </span>
                      )}
                      <b>{n.toLocaleString()}</b>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* 💰 AI별로 나눠 본다 — 합계 한 줄로는 조치계획서 ■10 의 칸을 못 채운다 */}
          <DocentCost list={top("npc_ask", 10)} periodSay={periodSay(period)} />
        </>
      ) : null}
    </section>
  );
}

/**
 * 🔴 **깨진 화면은 「어디서」만으로는 못 고친다.**
 *
 * 새로고침이면 풀리는 것(새 판이 올라와 옛 조각을 못 찾음)과 진짜 버그는 할 일이 완전히
 * 다른데, 예전엔 `/rally` 처럼 화면 이름만 남았다. 이제 종류도 같이 남는다 —
 * 읽는 법은 아이 화면과 **같은 함수**(shared `errorStatSay`).
 * 옛 기록(`/rally`)도 그대로 읽힌다.
 */
function ErrorSay({ statKey }: { statKey: string }) {
  const { where, kind, hint, site } = errorStatSay(statKey);
  return (
    <span>
      {where}
      {kind ? <b className="stats__kind"> · {kind}</b> : null}
      {/* 📍 터진 자리(우리 조각 이름). 옛 기록엔 없다 — 없으면 안 적는다. */}
      {site ? <code className="stats__site">{site}</code> : null}
      {hint ? <small className="stats__hint">{hint}</small> : null}
    </span>
  );
}

/**
 * 📖 **어느 책의 몇 쪽인가.**
 *
 * 예전엔 쪽 번호만 실려서 **세 책의 8쪽이 한 통에 섞였다** — 「8쪽에서 28번」이 34쪽짜리의
 * 중간인지 12쪽짜리의 끝인지 알 수가 없었다. 옛 기록(쪽 번호만)도 그대로 읽힌다.
 */
/**
 * 🎧 어느 책의 읽어주기인가. 더빙은 **돈이 드는** 것이라 「어디에 더 쓸까」가 이 줄의 쓸모다.
 * 옛 기록은 언어만 있다 — 없는 책 이름을 지어내지 않고 그렇게 적는다.
 */
function AudioSay({ statKey }: { statKey: string }) {
  const { book, lang } = audioStatSay(statKey);
  const say = lang === "en" ? "영어" : "한국어";
  return (
    <span>
      {book ? <b>{book}</b> : <i>(어느 책인지 안 남은 옛 기록)</i>} {say}
    </span>
  );
}

function BookPageSay({ statKey }: { statKey: string }) {
  const { book, page } = bookPageStatSay(statKey);
  /**
   * 🔴 **옛 「1쪽」에는 목록만 열어 본 것이 섞여 있다.** 책이 여럿이면 `/book` 은 고르는
   * 화면인데 거기서도 「1쪽 읽음」이 올라갔다(2026-09-02 고침). 그래서 1쪽이 210,
   * 2쪽이 44 로 **5배 낙차**처럼 보인다 — 「아이들이 1쪽에서 그만 읽는다」로 읽으면
   * 안 된다. 새 기록은 책 이름이 붙으므로 이 줄에 안 섞인다.
   */
  const listMixed = !book && page === "1";
  return (
    <span>
      {book ? <b>{book}</b> : <i>(어느 책인지 안 남은 옛 기록)</i>} {page}쪽
      {listMixed ? (
        <small className="stats__hint">고르는 화면만 열어 본 것도 섞여 있어요(옛 기록)</small>
      ) : null}
    </span>
  );
}
